import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import PageHelmet from '@/components/feature/PageHelmet';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-search-line text-2xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    url: `https://balnsj.cn/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '鬼父在线',
    },
    articleBody: article.excerpt,
  };

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: article.title,
    description: article.excerpt,
    url: `https://balnsj.cn/article/${article.slug}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://balnsj.cn/' },
        { '@type': 'ListItem', position: 2, name: article.category, item: `https://balnsj.cn/category/${article.categorySlug}` },
        { '@type': 'ListItem', position: 3, name: article.title },
      ],
    },
  };

  return (
    <>
      <ScrollToTop />
      <PageHelmet
        title={`${article.title} - 鬼父在线`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://balnsj.cn/article/${article.slug}`}
        ogType="article"
        jsonLd={[articleJsonLd, webPageJsonLd]}
      />

      <Navbar />

      <main className="pt-16">
        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: article.category, path: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-4">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> {article.readTime}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i> {article.views.toLocaleString()} 次阅读
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-6">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>

            <div
              className="article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>

          {relatedArticles.length > 0 && (
            <section className="border-t border-background-200 pt-10 mt-10">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="group rounded-xl overflow-hidden bg-background-100 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={related.title}
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-foreground-400">{related.date}</span>
                      <h3 className="text-sm font-semibold text-foreground-900 mt-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}